/**
 * galaxy-view.ts — strategic star map renderer.
 *
 * Reads GameState + UiState.galaxyCamera and draws the whole galaxy: stars,
 * their planets (as tiny orbiting dots), and ships (as owner-coloured
 * chevrons). Clips everything to the supplied viewport rectangle.
 *
 * This module is a pure reader — it never mutates state.
 */

import type { EmpireId, GameState, SystemId } from '../state/types';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';
import { WARP_GATE_CHASSIS } from '../game/balance-space-structures';

export function drawGalaxyView(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  ctx.save();
  // Clip galaxy drawing to the viewport so pans beyond the edge don't spill
  // over the HUD or panel.
  ctx.beginPath();
  ctx.rect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);
  ctx.clip();

  const selectedSysId = ui.selection.kind === 'system' ? ui.selection.id : null;

  // MFix6.3 — Galaxy-view fog-of-war: planet dots + system labels +
  // (in MFix6.4) per-empire icon stacks are gated on the viewer empire's
  // `scoutedSystemIds`. Computed once per frame; reused across the
  // system loop and the aggregate-icon loop. devRevealAll (F10 cheat)
  // bypasses the gate to make every system visible.
  const viewerEmpireForFog = state.empires.get(viewerEmpireId(state, ui));
  const isScouted = (sysId: SystemId): boolean =>
    ui.devRevealAll ? true : viewerEmpireForFog?.scoutedSystemIds.has(sysId) ?? false;

  // Sprite sizes — scale gently with zoom so systems feel weighty when zoomed
  // in but stay legible at minimum zoom.
  // MFix6.6 — star + planet-orbit bumped for galaxy-view readability.
  // Initial 10× was too aggressive in playtest; halved to ~5× the
  // pre-MFix6.6 baseline. SYSTEM_HIT_RADIUS_PX in input.ts matches.
  const starRadius = Math.max(25, 35 * Math.min(1.5, ui.galaxyCamera.zoom));
  const planetOrbitRadius = Math.max(90, 110 * Math.min(2, ui.galaxyCamera.zoom));
  const planetDotRadius = 5;

  // Hyperlane edges — drawn first so star sprites sit on top. Each pair is
  // drawn once (smaller-id end "owns" the line) so we don't double up.
  ctx.save();
  ctx.strokeStyle = 'rgba(120, 160, 230, 0.18)';
  ctx.lineWidth = 1;
  for (const jp of state.jumpPoints.values()) {
    if (jp.linkedSystemId === null) continue;
    if (jp.systemId >= jp.linkedSystemId) continue; // dedupe
    const aSys = state.systems.get(jp.systemId);
    const bSys = state.systems.get(jp.linkedSystemId);
    if (!aSys || !bSys) continue;
    const aP = worldToScreen(ui.galaxyCamera, vp, { x: aSys.x, y: aSys.y });
    const bP = worldToScreen(ui.galaxyCamera, vp, { x: bSys.x, y: bSys.y });
    ctx.beginPath();
    ctx.moveTo(aP.x, aP.y);
    ctx.lineTo(bP.x, bP.y);
    ctx.stroke();
  }
  ctx.restore();

  // v3M18 — Warp Gate connections. Owner-only red dotted lines drawn after
  // standard JPs so they read clearly when both exist. Live links full
  // alpha; building links lower-alpha so the player can see progress
  // visually without confusing them with completed connections.
  // Perpendicular offset where a natural JP-pair runs between the same
  // two systems, so the two lines render side-by-side.
  ctx.save();
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 4]);
  for (const link of state.warpGateLinks.values()) {
    if (
      !ui.devRevealAll &&
      link.ownerEmpireId !== viewerEmpireForFog?.id
    ) {
      continue;
    }
    const aSys = state.systems.get(link.systemAId);
    const bSys = state.systems.get(link.systemBId);
    if (!aSys || !bSys) continue;
    const aP = worldToScreen(ui.galaxyCamera, vp, { x: aSys.x, y: aSys.y });
    const bP = worldToScreen(ui.galaxyCamera, vp, { x: bSys.x, y: bSys.y });

    // Detect collision with an existing natural JP-pair between these
    // same two systems.
    let hasJpCollision = false;
    for (const jp of state.jumpPoints.values()) {
      if (jp.linkedSystemId === null) continue;
      if (
        (jp.systemId === link.systemAId &&
          jp.linkedSystemId === link.systemBId) ||
        (jp.systemId === link.systemBId &&
          jp.linkedSystemId === link.systemAId)
      ) {
        hasJpCollision = true;
        break;
      }
    }

    let ax = aP.x;
    let ay = aP.y;
    let bx = bP.x;
    let by = bP.y;
    if (hasJpCollision) {
      const dx = bP.x - aP.x;
      const dy = bP.y - aP.y;
      const len = Math.hypot(dx, dy);
      if (len > 0.001) {
        // Perpendicular unit vector × 5 px offset.
        const px = (-dy / len) * 5;
        const py = (dx / len) * 5;
        ax += px;
        ay += py;
        bx += px;
        by += py;
      }
    }

    ctx.strokeStyle =
      link.status === 'live'
        ? 'rgba(220, 60, 60, 0.6)'
        : 'rgba(220, 60, 60, 0.3)';
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();

  // v3M18 — precompute valid Connect Warp Gate target systems when the
  // player is in connect-mode. A target system is one that contains a
  // same-empire operational unconnected Warp Gate AND is not the
  // origin system (no self-links). Computed once per frame and reused
  // in the per-system loop below.
  const connectTargetSystems = new Set<SystemId>();
  if (ui.connectWarpGateMode) {
    const fromGateId = ui.connectWarpGateMode.fromGateId;
    const fromSystemId = ui.connectWarpGateMode.fromSystemId;
    const fromGate = state.ships.get(fromGateId);
    if (fromGate) {
      const ownerId = fromGate.ownerEmpireId;
      // Build a "this gate is already an endpoint of some link" lookup.
      const linkedGateIds = new Set<number>();
      for (const link of state.warpGateLinks.values()) {
        linkedGateIds.add(link.gateAId);
        linkedGateIds.add(link.gateBId);
      }
      for (const s of state.ships.values()) {
        if (s.id === fromGateId) continue;
        if (s.ownerEmpireId !== ownerId) continue;
        if (s.chassis !== WARP_GATE_CHASSIS) continue;
        if (s.systemId === null || s.systemId === fromSystemId) continue;
        if (linkedGateIds.has(s.id)) continue;
        connectTargetSystems.add(s.systemId);
      }
    }
  }

  for (const sys of state.systems.values()) {
    const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
    const isSelected = sys.id === selectedSysId;
    const isConnectTarget = connectTargetSystems.has(sys.id);

    // Soft glow behind the star — makes systems pop against the starfield.
    const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, starRadius * 3);
    glow.addColorStop(0, 'rgba(255, 210, 140, 0.35)');
    glow.addColorStop(1, 'rgba(255, 210, 140, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(p.x, p.y, starRadius * 3, 0, Math.PI * 2);
    ctx.fill();

    // Selection ring.
    if (isSelected) {
      ctx.beginPath();
      ctx.strokeStyle = '#8fffb0';
      ctx.lineWidth = 2;
      ctx.arc(p.x, p.y, starRadius + 8, 0, Math.PI * 2);
      ctx.stroke();
    }

    // v3M18 — Connect Warp Gate target ring. Pulsing red (animated via
    // tick parity to give a subtle "click me" feedback without holding
    // a separate animation state). Drawn on top of selection so the
    // player can still see which system was the origin.
    if (isConnectTarget) {
      const pulse = 1 + 0.15 * Math.sin(state.tick * 0.2);
      ctx.save();
      ctx.strokeStyle = 'rgba(220, 60, 60, 0.85)';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(p.x, p.y, (starRadius + 14) * pulse, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Star body.
    ctx.fillStyle = isSelected ? '#ffe8a0' : '#ffd27a';
    ctx.beginPath();
    ctx.arc(p.x, p.y, starRadius, 0, Math.PI * 2);
    ctx.fill();

    // Label. MFix6.3 — unscouted systems show "?" in place of their
    // name so the player can navigate the galaxy without leaking which
    // is which. Scouting (any empire ship enters the system) reveals
    // the name permanently. MFix6.6 fix-up — font bumped 12 → 20 px.
    // MFix6.6 fix-up #2 — moved from right of the star to above it so the
    // aggregate-icon column to the right has the full vertical span free
    // of label collision. Centre-aligned with bottom baseline puts the
    // text exactly above the star edge.
    const scouted = isScouted(sys.id);
    ctx.save();
    ctx.fillStyle = isSelected ? '#d8e4ff' : '#9fb3d9';
    ctx.font = '20px Inter, system-ui, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(scouted ? sys.name : '?', p.x, p.y - starRadius - 6);
    ctx.restore();

    // Planets orbiting the star — MFix6.3 hidden until the system is
    // scouted (no leak of "this system has 4 planets, this one has 1").
    if (scouted) {
      const planets = sys.planetIds;
      for (let i = 0; i < planets.length; i++) {
        const planet = state.planets.get(planets[i]!);
        if (!planet) continue;
        const angle = (i / Math.max(1, planets.length)) * Math.PI * 2;
        const px = p.x + Math.cos(angle) * planetOrbitRadius;
        const py = p.y + Math.sin(angle) * planetOrbitRadius;
        ctx.fillStyle = planet.ownerEmpireId !== null ? '#7acbff' : '#4a5a7a';
        ctx.beginPath();
        ctx.arc(px, py, planetDotRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Ships — drawn last so they float above systems. Filtered by fog-of-war:
  // own-empire ships always render; enemy ships only render if the player's
  // teamVision has a row for them. The row's `lastSeenSystemId` anchors
  // enemy chevrons to where they were last observed, so a ship that moved
  // during the grace window doesn't leak its new position to the galaxy
  // view. In v2 the same filter lights up cross-system structure scanners
  // and mid-jump detections automatically (see STRUCTURES v2 plan).
  //
  // Stationary ships stack to the upper-right of their system's star (slight
  // offset per ship so a 3-ship system doesn't look like a single chevron).
  // Mid-jump ships interpolate between their captured origin and target based
  // on each ship's own jumpDurationTotal — so a fast Scout visibly outpaces a
  // slow Cruiser on the same hyperlane. A short halo marks jumping ships.
  // Viewer = player under normal play, first non-player empire when the
  // F10 enemy-POV overlay is on (M8.1-F). Fog / "own ship" treatment flips
  // with it so galaxy view matches system view under the same toggle.
  const viewerId = viewerEmpireId(state, ui);
  const viewerVision = state.teamVision.get(viewerId);

  // MFix5.6 — per-system aggregate counts replace the per-ship chevron
  // stack. Mid-jump ships still render inline as hyperlane dots; they
  // don't belong to any system tally.
  // MFix6.4 — counts are per-empire-per-system so the panel renders
  // one icon row in each empire's color with their count, rather than
  // a single neutral icon. Outer key = systemId; inner key = empireId.
  const DEFENCE_PLATFORM_CHASSIS = new Set([
    'Energy Weapons Platform',
    'Kinetic Weapons Platform',
    'Concussion Weapons Platform',
  ]);
  const shipsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  const platformsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  function bumpEmpire(
    map: Map<SystemId, Map<EmpireId, number>>,
    sysId: SystemId,
    empireId: EmpireId,
  ): void {
    let inner = map.get(sysId);
    if (!inner) {
      inner = new Map();
      map.set(sysId, inner);
    }
    inner.set(empireId, (inner.get(empireId) ?? 0) + 1);
  }

  for (const ship of state.ships.values()) {
    const isOwn = ship.ownerEmpireId === viewerId;
    const rec = isOwn || ui.devRevealAll ? null : viewerVision?.get(ship.id);
    if (!isOwn && !ui.devRevealAll && !rec) continue;
    // Ghosts (seenVia=null) are stale memory; don't bump live counts.
    const ghost = !isOwn && rec?.seenVia === null;
    const empire = state.empires.get(ship.ownerEmpireId);
    const color = empire?.color ?? '#ff8f8f';

    let screenX: number;
    let screenY: number;
    let jumping = false;

    if (!isOwn && !ui.devRevealAll) {
      // Enemy — anchor to the last-seen system, not the ship's live
      // systemId. This preserves hidden info during the grace window.
      const lastSeenSys = rec!.lastSeenSystemId;
      const sys = state.systems.get(lastSeenSys);
      if (!sys) continue;
      if (!ghost) {
        bumpEmpire(shipsByEmpireBySystem, lastSeenSys, ship.ownerEmpireId);
        if (DEFENCE_PLATFORM_CHASSIS.has(ship.chassis)) {
          bumpEmpire(platformsByEmpireBySystem, lastSeenSys, ship.ownerEmpireId);
        }
      }
      // Skip the per-ship draw — aggregate render below handles this.
      continue;
    } else if (ship.systemId !== null) {
      const sys = state.systems.get(ship.systemId);
      if (!sys) continue;
      bumpEmpire(shipsByEmpireBySystem, ship.systemId, ship.ownerEmpireId);
      if (DEFENCE_PLATFORM_CHASSIS.has(ship.chassis)) {
        bumpEmpire(platformsByEmpireBySystem, ship.systemId, ship.ownerEmpireId);
      }
      // Skip per-ship draw; aggregate below.
      continue;
    } else if (
      ship.jumpOriginSystemId !== null &&
      ship.jumpTargetSystemId !== null &&
      ship.jumpDurationTotal > 0
    ) {
      const origin = state.systems.get(ship.jumpOriginSystemId);
      const target = state.systems.get(ship.jumpTargetSystemId);
      if (!origin || !target) continue;
      const t = 1 - ship.jumpTicksRemaining / ship.jumpDurationTotal;
      const wx = origin.x + (target.x - origin.x) * t;
      const wy = origin.y + (target.y - origin.y) * t;
      const p = worldToScreen(ui.galaxyCamera, vp, { x: wx, y: wy });
      screenX = p.x;
      screenY = p.y;
      jumping = true;
    } else {
      continue;
    }

    if (jumping) {
      // §8.2: in-transit ships render as a 3 px race-accent (here: empire-
      // color) dot with a soft halo to stand out against the hyperlane.
      // Player-owned transits get a faint 1 px trail behind them showing
      // recent travel; enemy transits don't (per §8.2 — only player units
      // give themselves away). Trail length is a fixed 16 screen-px;
      // longer would overlap the next system's halo on short lanes.
      ctx.save();
      // Soft halo first so it sits beneath the dot.
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.arc(screenX, screenY, 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // Player-only trail.
      if (isOwn && ship.jumpOriginSystemId !== null && ship.jumpTargetSystemId !== null) {
        const origin = state.systems.get(ship.jumpOriginSystemId);
        const target = state.systems.get(ship.jumpTargetSystemId);
        if (origin && target) {
          const op = worldToScreen(ui.galaxyCamera, vp, { x: origin.x, y: origin.y });
          const tp = worldToScreen(ui.galaxyCamera, vp, { x: target.x, y: target.y });
          const ldx = tp.x - op.x;
          const ldy = tp.y - op.y;
          const llen = Math.hypot(ldx, ldy) || 1;
          const trailLen = Math.min(16, llen * 0.4);
          const tailX = screenX - (ldx / llen) * trailLen;
          const tailY = screenY - (ldy / llen) * trailLen;
          ctx.save();
          const trailGrad = ctx.createLinearGradient(screenX, screenY, tailX, tailY);
          trailGrad.addColorStop(0, color);
          trailGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.strokeStyle = trailGrad;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(screenX, screenY);
          ctx.lineTo(tailX, tailY);
          ctx.stroke();
          ctx.restore();
        }
      }

      // 3 px filled dot at race-accent (here: empire) colour.
      ctx.save();
      if (ghost) ctx.globalAlpha = 0.45;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(screenX, screenY, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      continue;
    }
    // MFix5.6 — in-system ships are aggregated into per-system counts
    // above and rendered as icon+number rows below; the per-ship
    // chevron stack is gone.
  }

  // MFix5.6 — per-system aggregate indicators (replaces the chevron
  // stack). Rows of icon+count to the right of each star.
  // MFix6.4 — one row per (empire, category) pair, in the empire's
  // color. Categories: ships, planets, defence platforms.
  // MFix6.6 — anchor the indicator block past the (now-larger) star
  // sprite edge rather than at a fixed offset from centre.
  drawPerSystemIndicators(
    ctx,
    state,
    ui,
    vp,
    shipsByEmpireBySystem,
    platformsByEmpireBySystem,
    starRadius,
  );

  ctx.restore();
}

/** Gray used for the neutral-planet icon — matches the unowned planet-dot tone. */
const NEUTRAL_ICON_COLOR = 'rgba(160, 175, 200, 0.85)';

/**
 * Per-system aggregate indicators (MFix5.6 + MFix6.4). Renders one
 * icon+count row per (empire, category) pair beside each star, with
 * each empire's row in their `color`. Rows are skipped when the count
 * is zero so a quiet system doesn't clutter the view.
 *
 * MFix6.3 fog-of-war: only renders for systems the viewer has scouted
 * (or all systems when the F10 devRevealAll cheat is active).
 *
 * Within each system, rows are clustered by category (all ship rows,
 * then planet rows, then defence rows) so the player can scan by
 * category. Within a category, rows are ordered by empire id for
 * determinism. Neutral planets appear as a separate gray row beneath
 * the empire-owned planet rows.
 */
function drawPerSystemIndicators(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  shipsByEmpireBySystem: Map<SystemId, Map<EmpireId, number>>,
  platformsByEmpireBySystem: Map<SystemId, Map<EmpireId, number>>,
  starRadius: number,
): void {
  // Per-system planet ownership counts. Empire-owned: keyed by empire id.
  // Neutral (ownerEmpireId === null): keyed in a separate map. Walking
  // the full planet table once per frame is cheap at v1 scale (~30-60
  // planets total).
  const planetsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  const neutralPlanetsBySystem = new Map<SystemId, number>();
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) {
      neutralPlanetsBySystem.set(
        planet.systemId,
        (neutralPlanetsBySystem.get(planet.systemId) ?? 0) + 1,
      );
      continue;
    }
    let inner = planetsByEmpireBySystem.get(planet.systemId);
    if (!inner) {
      inner = new Map();
      planetsByEmpireBySystem.set(planet.systemId, inner);
    }
    inner.set(planet.ownerEmpireId, (inner.get(planet.ownerEmpireId) ?? 0) + 1);
  }

  ctx.save();
  // MFix6.6 fix-up #2 — count text trimmed 18 → 16 px after icons were
  // shrunk 25 %. Keeps visual weight balanced across the row.
  ctx.font = '16px Inter, system-ui, "Segoe UI", sans-serif';
  ctx.textBaseline = 'middle';

  // MFix6.3 — Aggregate indicators are gated on the viewer empire's
  // scouted set. Unscouted systems show no counts; you'd be inferring
  // troop levels otherwise. devRevealAll bypasses for the F10 cheat.
  const viewerForFog = state.empires.get(viewerEmpireId(state, ui));

  for (const sys of state.systems.values()) {
    const visible =
      ui.devRevealAll || (viewerForFog?.scoutedSystemIds.has(sys.id) ?? false);
    if (!visible) continue;

    const shipCounts = shipsByEmpireBySystem.get(sys.id);
    const platformCounts = platformsByEmpireBySystem.get(sys.id);
    const empirePlanetCounts = planetsByEmpireBySystem.get(sys.id);
    const neutralPlanets = neutralPlanetsBySystem.get(sys.id) ?? 0;

    const hasAny =
      (shipCounts && shipCounts.size > 0) ||
      (platformCounts && platformCounts.size > 0) ||
      (empirePlanetCounts && empirePlanetCounts.size > 0) ||
      neutralPlanets > 0;
    if (!hasAny) continue;

    const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
    // MFix6.6 fix-up #2 — icons trimmed 25 %, so row height drops
    // 24 → 20 px and the count-text offset drops 18 → 14 px (still
    // clears the widest icon — the ship triangle, now 18 px wide).
    const ROW_HEIGHT = 20;
    const COUNT_TEXT_DX = 14;
    const ax = p.x + starRadius + 16;
    let ay = p.y - ROW_HEIGHT / 2;

    // Ship rows (one per empire in id order).
    if (shipCounts) {
      for (const [empireId, count] of sortedEmpireEntries(shipCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawShipIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }

    // Planet rows — empire-owned first, then a neutral row at the end.
    if (empirePlanetCounts) {
      for (const [empireId, count] of sortedEmpireEntries(empirePlanetCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawPlanetIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }
    if (neutralPlanets > 0) {
      drawPlanetIcon(ctx, ax, ay, NEUTRAL_ICON_COLOR);
      ctx.fillStyle = NEUTRAL_ICON_COLOR;
      ctx.fillText(String(neutralPlanets), ax + COUNT_TEXT_DX, ay);
      ay += ROW_HEIGHT;
    }

    // Defence platform rows (one per empire in id order).
    if (platformCounts) {
      for (const [empireId, count] of sortedEmpireEntries(platformCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawDefenceIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }
  }
  ctx.restore();
}

/** Iterate empire-id-keyed entries in ascending id order — deterministic across frames. */
function sortedEmpireEntries(
  m: Map<EmpireId, number>,
): Array<[EmpireId, number]> {
  return [...m.entries()].sort((a, b) => a[0] - b[0]);
}

// MFix6.6 fix-up #2 — icon shapes trimmed 25 %: triangle 24×18 → 18×14,
// circle r=9 → r=7, square 18×18 → 14×14. Centred on (x, y); rendered
// with the empire's color (or NEUTRAL_ICON_COLOR for neutral planets).
function drawShipIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - 7);
  ctx.lineTo(x + 9, y + 7);
  ctx.lineTo(x - 9, y + 7);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawPlanetIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawDefenceIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 7, y - 7, 14, 14);
  ctx.restore();
}
